import React, { useEffect, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { faCheck, faComment, faEnvelope, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import emailjs from '@emailjs/browser';
import { toast } from 'react-toastify';
import { Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

function ContactUs() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 600, easing: 'ease-out', once: true });
  }, []);

  const validate = () => {
    const errs = {};
    if (!formData.firstName.trim()) errs.firstName = 'First Name is required';
    if (!formData.lastName.trim()) errs.lastName = 'Last Name is required';
    if (!formData.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      errs.email = 'Email is invalid';
    if (!formData.message.trim()) errs.message = 'Message is required';
    return errs;
  };

  const handleChange = e => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
    setErrors(prev => ({ ...prev, [id]: '' }));
  };

  const handleSubmit = e => {
    e.preventDefault();

    const validation = validate();
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    const service = process.env.REACT_APP_EMAILJS_SERVICE;
    const template = process.env.REACT_APP_EMAILJS_TEMPLATE;
    const user = process.env.REACT_APP_EMAILJS_USER;
    console.log({ service, template, user });

    setLoading(true);
    emailjs
      .send(service, template, formData, user)
      .then(() => {
        setSent(true);
        setFormData({ firstName: '', lastName: '', email: '', message: '' });
        setErrors({});
        setTimeout(() => setSent(false), 2000);
      })
      .catch(err => {
        console.error('EmailJS Error:', err);
        toast.error('Failed to send message. Please try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  };


  return (
    <>
      <Helmet>
        <title>Contact Us | On Point Services</title>
        <meta
          name="description"
          content="Get in touch with On Point Services for fast, reliable maintenance and renovation services across Alabama, Florida, Georgia, and Tennessee."
        />
        <meta property="og:title" content="Contact Us | On Point Services" />
        <meta
          property="og:description"
          content="Contact OnPoint Services today to schedule top-quality handyman, plumbing, and electrical services in your area."
        />
        <link rel="canonical" href="https://www.on-point-services.com/contactus" />
      </Helmet>

    <div id="contact" className="contact-us-section py-5" data-aos="fade-up">
      <div className="container">
        <div className="section-header text-center mb-5" data-aos="fade-down">
          <h2
            className="display-5 poppins-semibold"
            data-aos="fade-down"
            data-aos-delay="100"
          >
            Contact <span>Us</span>
          </h2>
          <p className="text-muted" data-aos="fade-down" data-aos-delay="200">
            We’d love to hear from you. Fill out the form below and we’ll get
            back to you promptly.
          </p>
        </div>

        <div
          className="form-card mx-auto"
          data-aos="zoom-in"
          data-aos-delay="300"
        >
          <form className="row g-3" onSubmit={handleSubmit} noValidate>
            {/** First Name **/}
            <div
              className="col-md-6 form-floating position-relative"
              data-aos="fade-right"
              data-aos-delay="400"
            >
              <FontAwesomeIcon icon={faUser} className="floating-icon" />
              <input
                type="text"
                id="firstName"
                className={`form-control ${errors.firstName ? 'is-invalid' : ''
                  }`}
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                disabled={sent}
              />
              <label htmlFor="firstName">First Name</label>
              {errors.firstName && (
                <div className="invalid-feedback">{errors.firstName}</div>
              )}
            </div>

            {/** Last Name **/}
            <div
              className="col-md-6 form-floating position-relative"
              data-aos="fade-left"
              data-aos-delay="500"
            >
              <FontAwesomeIcon icon={faUser} className="floating-icon" />
              <input
                type="text"
                id="lastName"
                className={`form-control ${errors.lastName ? 'is-invalid' : ''
                  }`}
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                disabled={sent}
              />
              <label htmlFor="lastName">Last Name</label>
              {errors.lastName && (
                <div className="invalid-feedback">{errors.lastName}</div>
              )}
            </div>
            <div
              className="col-12 form-floating position-relative"
              data-aos="fade-right"
              data-aos-delay="600"
            >
              <FontAwesomeIcon icon={faEnvelope} className="floating-icon" />
              <input
                type="email"
                id="email"
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                disabled={sent}
              />
              <label htmlFor="email">Email Address</label>
              {errors.email && (
                <div className="invalid-feedback">{errors.email}</div>
              )}
            </div>

            {/** Message **/}
            <div
              className="col-12 form-floating position-relative"
              data-aos="fade-left"
              data-aos-delay="700"
            >
              <FontAwesomeIcon icon={faComment} className="floating-icon" />
              <textarea
                id="message"
                className={`form-control ${errors.message ? 'is-invalid' : ''
                  }`}
                placeholder="Your Message"
                style={{ height: '150px' }}
                value={formData.message}
                onChange={handleChange}
                disabled={sent}
              />
              <label htmlFor="message">Your Message</label>
              {errors.message && (
                <div className="invalid-feedback">{errors.message}</div>
              )}
            </div>

            <div
              className="col-12 text-center mt-3"
            >
              <button
                type="submit"
                className="btn btn-orange btn-lg px-5"
                disabled={sent}
              >
                {loading ? (
                  <Spinner animation='border' size="sm" />
                ) : sent ? (
                  'Message Sent'
                ) : (
                  'Send Message'
                )}
              </button>

            </div>
          </form>
        </div>
      </div>

    </div>
      <div className='careers' data-aos="fade-up" data-aos-delay="400">
  <div className='careers-title poppins-bold' data-aos="fade-up" data-aos-delay="400">
    Careers
  </div>

  <div className='careers-content' data-aos="fade-up" data-aos-delay="600">
    At On Point Services, we believe our team is the key to success. We're always looking for 
    <span className="careers-highlight"> passionate, motivated, and skilled individuals </span> 
    who want to grow alongside us.
    <br /><br />
    <span className="careers-offer">We Offer:</span>
    <div className='mt-4 offer'>
      
      <div className='d-flex flex-column me-2 '>
        <div>
          <FontAwesomeIcon icon={faCheck} className='check'/> 
        </div>
        <div>
          <FontAwesomeIcon icon={faCheck} className='check'/> 
        </div>
        <div>
          <FontAwesomeIcon icon={faCheck} className='check'/> 
        </div>
        <div>
          <FontAwesomeIcon icon={faCheck} className='check'/> 
        </div>
        </div>


        <div className='d-flex flex-column'>
        <div>
          Flexible schedules tailored to your lifestyle
        </div>
        <div>
          Competitive salaries
        </div>
        <div>
        Premium medical insurance
        </div>
        <div>
        Comprehensive retirement plans
        </div>
        </div>
    </div>
    <br />
    If you're ready to make an impact and be a valued team member, send your CV to:
    <br />
    <a href="mailto:careers@on-point-services.com" className="careers-email">
      careers@on-point-services.com
    </a>
  </div>
</div>
    </>
  );
}

export default ContactUs;
